import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    MessageBar,
    MessageBarBody,
    Text,
    tokens,
    Tree,
    TreeItem,
    TreeItemLayout,
} from '@fluentui/react-components'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { useTranslation } from 'react-i18next'
import { AxiosInstance } from 'axios'
import useAxios from '../../../hooks/useAxios'
import LoadingIcon from '../../../components/Icon/LoadingIcon'
import { DRIVE_ITEM_CONFIG } from './folderPath'

type Props = {
    /** Folder the browse is anchored to; the user cannot navigate above it. Undefined is the drive root. */
    rootId?: string
    /** Path of the anchor, shown as the top entry so it can be picked again. */
    rootLabel: string
    /** Folder currently in use, so reopening the dialog starts from what the field already shows. */
    selectedId?: string
    /** Reports the folder the new folder should live in. Undefined means the anchor itself. */
    onSelect: (parentId: string | undefined) => void
    onDismiss: () => void
}

const useStyles = makeStyles({
    tree: {
        maxHeight: '320px',
        overflowY: 'auto',
    },
    subtle: {
        color: tokens.colorNeutralForeground3,
    },
})

/** Children are fetched a level at a time. Crawling the whole drive up front throttles the tenant. */
const fetchFolders = (axios: AxiosInstance, parentId?: string): Promise<DriveItem[]> =>
    axios
        .get<{ value: DriveItem[] }>(parentId ? `/items/${parentId}/children` : '/root/children', DRIVE_ITEM_CONFIG)
        .then(response => (response.data.value ?? []).filter(item => !!item.folder))

function FolderTreeItem(props: { folder: DriveItem; selectedId?: string; onSelect: (folderId: string) => void }): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const [children, setChildren] = useState<DriveItem[]>()
    const [open, setOpen] = useState<boolean>(false)
    const [failed, setFailed] = useState<boolean>(false)

    // Only load once the user actually opens this branch.
    useEffect(() => {
        if (!open || children || failed) {
            return
        }
        fetchFolders(axios, props.folder.id)
            .then(setChildren)
            .catch(() => setFailed(true))
    }, [open])

    return (
        <TreeItem itemType={'branch'} value={props.folder.id} open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <TreeItemLayout
                onClick={() => props.onSelect(props.folder.id)}
                aside={
                    props.selectedId === props.folder.id ? (
                        <Text size={200} weight={'semibold'}>
                            {translate('base.folderpath.browse.selected')}
                        </Text>
                    ) : undefined
                }
            >
                {props.folder.name}
            </TreeItemLayout>
            <Tree>
                {failed && (
                    <TreeItem itemType={'leaf'}>
                        <TreeItemLayout>
                            <Text className={styles.subtle} size={200}>
                                {translate('base.folderpath.browse.error')}
                            </Text>
                        </TreeItemLayout>
                    </TreeItem>
                )}
                {!failed && !children && (
                    <TreeItem itemType={'leaf'}>
                        <TreeItemLayout>
                            <LoadingIcon />
                        </TreeItemLayout>
                    </TreeItem>
                )}
                {children?.length === 0 && (
                    <TreeItem itemType={'leaf'}>
                        <TreeItemLayout>
                            <Text className={styles.subtle} size={200}>
                                {translate('base.folderpath.browse.empty')}
                            </Text>
                        </TreeItemLayout>
                    </TreeItem>
                )}
                {children?.map(child => (
                    <FolderTreeItem key={child.id} folder={child} selectedId={props.selectedId} onSelect={props.onSelect} />
                ))}
            </Tree>
        </TreeItem>
    )
}

/**
 * Lets the user put the folder somewhere deeper instead of typing a path. It picks the *parent*
 * folder, never the folder itself — "Zaken is selected" would otherwise be ambiguous between
 * "use Zaken" and "create inside Zaken". Pointing at an existing folder stays a matter of typing
 * its name, which the field then offers to adopt.
 *
 * Browsing is anchored to rootId so a dossier can never be re-parented out of its CRM relation.
 */
export default function FolderBrowseDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const [folders, setFolders] = useState<DriveItem[]>()
    /**
     * Undefined means the anchor itself is selected. It starts on whatever the field already uses,
     * so confirming without touching anything keeps that folder instead of silently moving the
     * new folder back up to the anchor.
     */
    const [selected, setSelected] = useState<string | undefined>(props.selectedId === props.rootId ? undefined : props.selectedId)
    const [error, setError] = useState<boolean>(false)

    useEffect(() => {
        fetchFolders(axios, props.rootId)
            .then(setFolders)
            .catch(() => setError(true))
    }, [props.rootId])

    return (
        <Dialog open onOpenChange={(_, data) => !data.open && props.onDismiss()}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{translate('base.folderpath.browse.title')}</DialogTitle>
                    <DialogContent>
                        {error && (
                            <MessageBar intent={'error'}>
                                <MessageBarBody>{translate('base.folderpath.browse.error')}</MessageBarBody>
                            </MessageBar>
                        )}
                        {!error && !folders && <LoadingIcon />}
                        {folders && (
                            <Tree className={styles.tree} aria-label={translate('base.folderpath.browse.title')}>
                                <TreeItem itemType={'leaf'} value={'__root__'}>
                                    <TreeItemLayout
                                        onClick={() => setSelected(undefined)}
                                        aside={
                                            !selected ? (
                                                <Text size={200} weight={'semibold'}>
                                                    {translate('base.folderpath.browse.selected')}
                                                </Text>
                                            ) : undefined
                                        }
                                    >
                                        {props.rootLabel}
                                    </TreeItemLayout>
                                </TreeItem>
                                {folders.map(folder => (
                                    <FolderTreeItem key={folder.id} folder={folder} selectedId={selected} onSelect={setSelected} />
                                ))}
                            </Tree>
                        )}
                        {folders?.length === 0 && (
                            <Text className={styles.subtle} size={200}>
                                {translate('base.folderpath.browse.empty')}
                            </Text>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance={'secondary'} onClick={props.onDismiss}>
                            {translate('action.cancel')}
                        </Button>
                        <Button appearance={'primary'} onClick={() => props.onSelect(selected)}>
                            {translate('base.folderpath.browse.confirm')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
